import React, {CSSProperties, ReactNode} from 'react';
import clsx from 'clsx';
import {PlacementAxis} from '@react-types/overlays';
import {m} from 'framer-motion';
import {
  OverlayProps,
  useModal,
  useOverlay,
  usePreventScroll,
} from '@react-aria/overlays';
import {mergeProps, useObjectRef} from '@react-aria/utils';
import {popoverStyle} from './popover-style';
import {PopoverAnimation} from './popover-animation';
import {Overlay} from '../overlay';

export interface PopoverProps extends OverlayProps {
  children: ReactNode;
  style?: CSSProperties;
  hideArrow?: boolean;
  isNonModal?: boolean;
  preventScroll?: boolean;
}

export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
  (props, ref) => {
    const {
      children,
      onClose,
      isOpen,
      shouldCloseOnBlur,
      hideArrow,
      isKeyboardDismissDisabled,
      isNonModal,
      isDismissable = true,
      preventScroll = true,
      ...otherProps
    } = props;
    usePreventScroll({isDisabled: !preventScroll});
    return (
      <Overlay type="popover">
        <PopoverWrapper
          {...otherProps}
          ref={ref}
          shouldCloseOnBlur={shouldCloseOnBlur}
          isKeyboardDismissDisabled={isKeyboardDismissDisabled}
          hideArrow={hideArrow}
          isNonModal={isNonModal}
          isDismissable={isDismissable}
          isOpen={isOpen}
          onClose={onClose}
        >
          {children}
        </PopoverWrapper>
      </Overlay>
    );
  }
);

const PopoverWrapper = React.forwardRef<HTMLDivElement, PopoverProps>(
  (props, ref) => {
    const {
      children,
      isOpen,
      onClose,
      hideArrow,
      shouldCloseOnBlur,
      isKeyboardDismissDisabled,
      isNonModal,
      isDismissable,
      ...otherProps
    } = props;

    const objRef = useObjectRef(ref);

    // Handle interacting outside the dialog and pressing
    // the Escape key to close the modal.
    const {overlayProps} = useOverlay(
      {...props, isDismissable: isDismissable && isOpen},
      objRef
    );

    // Hide content outside the modal from screen readers.
    const {modalProps} = useModal({
      isDisabled: isNonModal,
    });

    return (
      // @ts-ignore
      <m.div
        ref={objRef}
        {...PopoverAnimation}
        {...mergeProps(otherProps, overlayProps, modalProps)}
        className={popoverStyle}
        role="presentation"
      >
        {children}
        {hideArrow ? null : <Arrow popoverPlacement="bottom" />}
      </m.div>
    );
  }
);

type ArrowProps = {
  popoverPlacement: PlacementAxis;
};
function Arrow({popoverPlacement}: ArrowProps) {
  const className = clsx(
    'absolute fill-background',
    popoverPlacement === 'top' && '-translate-x-1/2 left-1/2',
    popoverPlacement === 'bottom' && '-translate-x-1/2 left-1/2 rotate-180',
    popoverPlacement === 'left' && '-translate-y-1/2 top-1/2 rotate-90',
    popoverPlacement === 'right' && '-translate-y-1/2 top-1/2 -rotate-90'
  );

  return (
    <svg
      className={className}
      width="16"
      height="8"
      viewBox="0 0 30 10"
      preserveAspectRatio="none"
    >
      <polygon points="0,0 30,0 15,10" />
    </svg>
  );
}
